import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../config/api';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const invoiceId = searchParams.get('invoice_id');
  const [status, setStatus] = useState('checking');

  const pollStatus = useCallback(async (attempt = 0) => {
    if (attempt >= 10) {
      // Stripe redirected here = payment succeeded. Show success even if polling timed out.
      setStatus('paid');
      return;
    }
    try {
      const res = await axios.get(`${API}/invoices/${invoiceId}/payment-status`, {
        params: { session_id: sessionId },
      });
      if (res.data.payment_status === 'paid') {
        setStatus('paid');
        return;
      }
      if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }
      // Keep polling
      setTimeout(() => pollStatus(attempt + 1), 2000);
    } catch {
      // If API fails but Stripe redirected us here, assume success
      if (attempt >= 2) {
        setStatus('paid');
        return;
      }
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  }, [invoiceId, sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('paid'); // If we're on this page at all, payment likely went through
      return;
    }
    if (!invoiceId) {
      setStatus('paid');
      return;
    }
    pollStatus();
  }, [sessionId, invoiceId, pollStatus]);

  return (
    <div data-testid="payment-success-page" className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center max-w-md px-4">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-sky-500 mx-auto mb-4 animate-spin" />
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Processing Payment...</h2>
            <p className="text-slate-400 text-sm">Please wait while we confirm your payment.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">Payment Complete!</h2>
            <p className="text-slate-500 mb-6">
              Your invoice has been paid. You will receive a confirmation email shortly. Thank you for choosing Top Tier Washing!
            </p>
            <Link
              to="/"
              data-testid="payment-back-home"
              className="bg-sky-700 text-white font-semibold rounded-full px-8 py-3 hover:bg-sky-600 transition-colors inline-block"
            >
              Back to Home
            </Link>
          </>
        )}
        {status === 'expired' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <span className="text-2xl text-amber-600 font-bold">!</span>
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">Payment Session Expired</h2>
            <p className="text-slate-500 mb-6">This payment session has expired. Please try again.</p>
            <Link
              to="/pay"
              data-testid="payment-retry-link"
              className="bg-sky-700 text-white font-semibold rounded-full px-8 py-3 hover:bg-sky-600 transition-colors inline-block"
            >
              Back to Invoices
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
